import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { createFlatSpacingFields, readBoxSpacing, PX_AI_SCHEMA } from '../fields/spacing-fields';
import { renderFieldGroups } from '../fields/field-groups';
import type { FieldGroupDefinition } from '../fields/field-groups';
import { selectField } from '../fields/widgets/select-field';
import { toggleField } from '../fields/widgets/toggle-field';
import { NumberField } from '../fields/widgets/number-field';
import { BorderColorField } from '../fields/widgets/border-color-field';
import { BORDER_COLOR_AI_SCHEMA, borderStyle } from '../fields/border';
import type { BorderColor, BorderValue } from '../fields/border';
import { BLOCK_SPACING, CONTAINER_WIDTHS, lookup, spacingToCss } from '../fields/tokens';
import type { FlatSpacingProps } from '../fields/spacing-fields';
import type { BoxSpacing } from '../fields/tokens';
import {
  createFlatMobilePaddingFields,
  resolveFlatMobilePaddingFields,
  fillFlatMobilePadding,
} from '../fields/mobile-padding';
import type { FlatMobilePaddingProps } from '../fields/mobile-padding';
import {
  BackgroundField,
  BACKGROUND_AI_SCHEMA,
  BACKGROUND_IMAGE_FIELDS,
  DEFAULT_BACKGROUND,
  DEFAULT_BACKGROUND_IMAGE_PROPS,
  backgroundImageStyle,
  backgroundOverlayStyle,
  BackgroundOverlay,
  hasBackgroundImage,
  resolveBackgroundImageFields,
} from '../fields';
import type { BackgroundImageProps, BackgroundValue } from '../fields';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  backgroundStyleWithOpacity,
  backgroundValueColor,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';

import { starterChild } from './slot';
import type { SlotProps, StarterChild } from './slot';

/** Vertical rhythm between the section's direct children. */
const CHILD_GAP = BLOCK_SPACING.sm;

/** Section's own padding default: roomier than a leaf block, so it reads as a page band. */
const DEFAULT_PADDING: BoxSpacing = { top: '48px', right: '24px', bottom: '48px', left: '24px' };

// Keep the shared border default unchanged; enabling Section's border should immediately show it.
export const DEFAULT_SECTION_BORDER: BorderValue = { color: { style: 'theme' }, width: '1px' };

const CONTENT_ALIGNMENT = {
  left: '0 auto 0 0',
  center: '0 auto',
  right: '0 0 0 auto',
} as const;

/**
 * Generic Section defaults to transparent so new sections blend into the page-level
 * background without painting an unintentional opaque white fill.
 * Header/Footer starter layouts explicitly specify Surface background where intended.
 */
export const DEFAULT_SECTION_BACKGROUND: BackgroundValue = {
  ...DEFAULT_BACKGROUND,
  color: 'none',
};

export type SectionProps = BackgroundImageProps &
  FlatMobilePaddingProps &
  FlatSpacingProps<'padding'> & {
    /**
     * Puck injects this slot component at render time. `defaultProps.content` holds the starter
     * layout's child data instead (an array), which Puck consumes at insert time and replaces with
     * the injected component before `render` ever sees it.
     */
    content?: React.FC<SlotProps>;
    background: BackgroundValue;
    backgroundOpacity: number;
    contentAlignment: keyof typeof CONTENT_ALIGNMENT;
    showBorder: boolean;
    radius: 'none' | 'theme';
    /** A `CONTAINER_WIDTHS` key, or a raw CSS length carried over from before this was a token. */
    maxWidth: string;
    /** Settings retained while Border is off, so toggling it back on restores the chosen appearance. */
    borderColor: BorderColor;
    borderWidth: string;
  };

const SECTION_FIELD_GROUPS: readonly FieldGroupDefinition<keyof SectionProps>[] = [
  {
    label: 'Appearance',
    fields: [
      'backgroundImage',
      'backgroundSize',
      'backgroundPosition',
      'backgroundRepeat',
      'overlayOpacity',
      'background',
      'backgroundOpacity',
      'showBorder',
      'borderColor',
      'borderWidth',
      'radius',
    ],
  },
  { label: 'Layout', fields: ['maxWidth', 'contentAlignment'] },
  {
    label: 'Spacing',
    fields: [
      { label: 'Padding', variant: 'card', fields: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] },
      'differentMobilePadding',
      {
        label: 'Mobile padding',
        variant: 'card',
        fields: ['mobilePaddingTop', 'mobilePaddingRight', 'mobilePaddingBottom', 'mobilePaddingLeft'],
      },
    ],
  },
];

/**
 * Resolves the stored max width.
 *
 * Accepts a token key or a literal, mirroring `resolveTextColor`: pages authored before this was a
 * token hold raw lengths like `'800px'`, and silently re-capping them to a preset would visibly
 * reflow live content.
 */
function resolveMaxWidth(value: string): string {
  return lookup(CONTAINER_WIDTHS, value, value);
}

// Starter layout: a heading and paragraph give new authors something to act on immediately.
//
// Each child is built from its own component's `defaultProps` via `starterChild()`, overriding only
// the copy. That completeness is load-bearing, not tidiness: Puck merges `defaultProps` for the
// inserted Section but not for its slot children, and the field panel reads child props unmerged,
// so a prop left absent here shows an empty control and corrupts `object`-field edits. See
// `StarterChild` in ./slot for the full mechanism, and `editor/default-layout-parity.test.tsx`
// for the test that enforces it.
//
// id is intentionally absent — populateIds() stamps { id: generated, ...item.props }, so a
// hardcoded id would win and produce duplicate ids across every inserted Section instance.
const SECTION_STARTER_CHILDREN: StarterChild[] = [
  starterChild('Heading', { text: '<p>Your section heading</p>' }),
  starterChild('Paragraph', { text: '<p>Add your content here.</p>' }),
];

const SectionContainer = styled.div`
  padding: var(--cms-section-padding, 0px);

  @media ${mediaQueries.largePhone} {
    padding: var(--cms-section-mobile-padding, var(--cms-section-padding, 0px));
  }
`;

/** Full-width container that groups content in a padded, optionally capped column. */
export const sectionConfig: ComponentConfig<SectionProps> = {
  label: 'Section',
  ai: {
    instructions:
      "A full-width container that groups content. IMPORTANT: Always add child components (Heading, Text, Image, Button) INTO this component's content slot — never leave it empty. A Section without children is useless.",
  },
  fields: {
    content: { type: 'slot' },
    ...BACKGROUND_IMAGE_FIELDS,
    background: {
      type: 'custom',
      label: 'Background color',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA,
        instructions:
          "Fallback color shown when there is no image or it fails to load. Set color to a surface role ('none' is transparent, 'surface', 'muted') so it follows merchant branding; use a hex value only when a specific brand color is required.",
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    showBorder: toggleField('Border'),
    borderColor: {
      type: 'custom',
      label: 'Border color',
      render: BorderColorField,
      ai: {
        schema: BORDER_COLOR_AI_SCHEMA,
        instructions:
          "Either 'none', 'theme' (the storefront hairline border), or a custom hex. Prefer 'theme' or 'none'.",
      },
    },
    borderWidth: {
      type: 'custom',
      label: 'Border width',
      render: NumberField('px', DEFAULT_SECTION_BORDER.width),
      ai: { schema: PX_AI_SCHEMA, instructions: 'Border width, e.g. 1px.' },
    },
    radius: {
      type: 'radio',
      label: 'Corner radius',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Theme card radius', value: 'theme' },
      ],
      ai: { schema: { type: 'string', enum: ['none', 'theme'] } },
    },
    // selectField rather than a native `select`: sections authored before this was a token hold a raw
    // length like '800px', and a native select with no matching option silently displays the first
    // one -- claiming 'Narrow' for a page rendering at 800px. The widget surfaces the stored value
    // instead.
    maxWidth: selectField(
      'Width',
      [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'Wide', value: 'wide' },
        { label: 'Full width', value: 'none' },
      ],
      "Caps the section container's width. 'narrow' (~768px) suits a focused column; 'normal' (~992px) is a comfortable mid-width band; 'wide' tracks the storefront's configured site width so the section aligns with the header and product grid; 'none' is full-bleed."
    ),
    contentAlignment: {
      type: 'radio',
      label: 'Content alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
    },
    ...createFlatSpacingFields('padding'),
    ...createFlatMobilePaddingFields(),
  },
  renderFields: ({ fields }) => renderFieldGroups(fields, SECTION_FIELD_GROUPS),
  resolveData: ({ props }) => ({ props: fillFlatMobilePadding(props, DEFAULT_PADDING) }),
  resolveFields: (data, { fields }) => {
    const resolvedFields = resolveFlatMobilePaddingFields(
      data.props.differentMobilePadding,
      resolveBackgroundImageFields(
        data.props.backgroundImage,
        resolveBackgroundOpacityFields(backgroundValueColor(data.props.background), fields)
      )
    );
    // Stored JSON can violate the boolean type; match the toggle's strict true check.
    if ((data.props.showBorder as unknown) === true) return resolvedFields;

    const { borderColor: _borderColor, borderWidth: _borderWidth, ...withoutBorder } = resolvedFields;
    return withoutBorder as Fields<SectionProps>;
  },
  defaultProps: {
    ...DEFAULT_BACKGROUND_IMAGE_PROPS,
    background: DEFAULT_SECTION_BACKGROUND,
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    contentAlignment: 'center',
    paddingTop: DEFAULT_PADDING.top,
    paddingRight: DEFAULT_PADDING.right,
    paddingBottom: DEFAULT_PADDING.bottom,
    paddingLeft: DEFAULT_PADDING.left,
    differentMobilePadding: false,
    mobilePaddingTop: DEFAULT_PADDING.top,
    mobilePaddingRight: DEFAULT_PADDING.right,
    mobilePaddingBottom: DEFAULT_PADDING.bottom,
    mobilePaddingLeft: DEFAULT_PADDING.left,
    maxWidth: 'none',
    showBorder: false,
    radius: 'none',
    borderColor: DEFAULT_SECTION_BORDER.color,
    borderWidth: DEFAULT_SECTION_BORDER.width,
    content: (SECTION_STARTER_CHILDREN as unknown) as React.FC<SlotProps>,
  },
  render: ({
    content: slotContent,
    backgroundImage = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundImage,
    backgroundSize = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundSize,
    backgroundPosition = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundPosition,
    backgroundRepeat = DEFAULT_BACKGROUND_IMAGE_PROPS.backgroundRepeat,
    overlayOpacity = DEFAULT_BACKGROUND_IMAGE_PROPS.overlayOpacity,
    background = DEFAULT_SECTION_BACKGROUND,
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    contentAlignment = 'center',
    differentMobilePadding = false,
    maxWidth = 'none',
    showBorder = false,
    radius = 'none',
    borderColor = DEFAULT_SECTION_BORDER.color,
    borderWidth = DEFAULT_SECTION_BORDER.width,
    ...spacingProps
  }) => {
    if (!slotContent) return <></>;
    const padding = readBoxSpacing(spacingProps, 'padding');
    const mobilePadding = readBoxSpacing(
      fillFlatMobilePadding({ ...spacingProps, differentMobilePadding }, DEFAULT_PADDING),
      'mobilePadding'
    );
    const Slot = slotContent;
    const alignmentKey = contentAlignment === 'left' || contentAlignment === 'right' ? contentAlignment : 'center';
    const margin = CONTENT_ALIGNMENT[alignmentKey];
    const hasImage = hasBackgroundImage(backgroundImage);

    return (
      <SectionContainer
        style={
          {
            ...backgroundStyleWithOpacity(background, backgroundOpacity),
            ...backgroundImageStyle({ backgroundImage, backgroundSize, backgroundPosition, backgroundRepeat }),
            ...(hasImage ? { position: 'relative', isolation: 'isolate' } : {}),
            ...((showBorder as unknown) === true ? borderStyle({ color: borderColor, width: borderWidth }) : {}),
            // Only rounded Sections clip descendants; square Sections keep their existing overflow behavior.
            ...(radius === 'theme' ? { borderRadius: 'var(--dcm-radius-cards, 12px)', overflow: 'hidden' } : {}),
            '--cms-section-padding': spacingToCss(padding),
            // Always set both variables so nested Sections cannot inherit a parent's mobile override.
            '--cms-section-mobile-padding': spacingToCss(
              (differentMobilePadding as unknown) === true ? mobilePadding : padding
            ),
            boxSizing: 'border-box',
            width: '100%',
          } as React.CSSProperties
        }
      >
        {hasImage && (
          <BackgroundOverlay
            data-testid='cms-section-overlay'
            aria-hidden='true'
            style={backgroundOverlayStyle(overlayOpacity)}
          />
        )}
        <div style={{ maxWidth: resolveMaxWidth(maxWidth), margin, position: 'relative', zIndex: 1 }}>
          <Slot
            minEmptyHeight={120}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: CHILD_GAP }}
          />
        </div>
      </SectionContainer>
    );
  },
};
